import { MODULE_KEYS } from "@/config/modules-registry";

/** Administration menu labels → Spatie permission names (match backend RestrictsAdminAccess). */
export const ADMIN_ASSETS_MENU_PERMISSIONS = {
  Assets: "Asset.Read",
  "Asset Categories": "Asset.Read",
};

export const ADMIN_SETTINGS_MENU_PERMISSIONS = {
  Users: "User.Read",
  "Roles & Permissions": "Role.Read",
  Profile: "User.Read",
  Security: "User.Read",
  "Email Settings": "User.Read",
  "Email Templates": "User.Read",
  Cronjob: "User.Read",
  Storage: "User.Read",
  "Ban IP Address": "User.Read",
  "Clear Cache": "User.Read",
  "General Settings": "User.Read",
  "System Settings": "User.Read",
  "Other Settings": "User.Read",
  Settings: "User.Read",
  "User Management": "User.Read",
};

export const ADMIN_REPORTS_MENU_PERMISSIONS = {
  Reports: "Reports.Read",
  "P9 Report": "Reports.Read",
  "Imported Legacy P9A": "Reports.Read",
  "PAYE (P10)": "Reports.Read",
  NSSF: "Reports.Read",
  "SHIF / SHA / NHIF": "Reports.Read",
  "Affordable Housing Levy": "Reports.Read",
  "Expense Report": "Reports.Read",
  "Invoice Report": "Reports.Read",
  "Payment Report": "Reports.Read",
  "Project Report": "Reports.Read",
  "Task Report": "Reports.Read",
  "Payslip Report": "Reports.Read",
  "Muster Roll Report": "Reports.Read",
  "Bank Transfer Report": "Reports.Read",
  "Cash Payroll Report": "Reports.Read",
  "Cash Payroll Audit": "Reports.Read",
  "Statutory Deduction Report": "Reports.Read",
  "Comparative Report": "Reports.Read",
  "Control Summary Report": "Reports.Read",
  "Loans & Savings Report": "Reports.Read",
  "Member Savings Report": "Reports.Read",
  "Loan Disbursement Report": "Reports.Read",
  "Loan Repayment Report": "Reports.Read",
  "Interest Earned Report": "Reports.Read",
  "Monthly Collection Report": "Reports.Read",
  "Attendance Report": "Reports.Read",
  "User Report": "Reports.Read",
  "Employee Report": "Reports.Read",
  "Leave Report": "Reports.Read",
  "Daily Report": "Reports.Read",
  "Audit Trail Report": "Reports.Read",
  "Imported Payroll History": "Reports.Read",
  "Annual Reports": "Reports.Read",
  "Monthly Reports": "Reports.Read",
  "Adhoc Reports": "Reports.Read",
};

export const ADMIN_ASSETS_ROUTE_RULES = [
  {
    permission: "Asset.Read",
    moduleKey: MODULE_KEYS.ADMIN_ASSETS,
    matches: (path) =>
      path === "/asset/assets" || path === "/asset/asset-categories",
  },
];

export const ADMIN_SETTINGS_ROUTE_RULES = [
  {
    permission: "User.Read",
    moduleKey: MODULE_KEYS.ADMIN_SETTINGS,
    matches: (path) =>
      path === "/user-management/users" ||
      path === "/user-management/roles-permissions" ||
      path === "/user-management/permission",
  },
  {
    permission: "User.Read",
    moduleKey: MODULE_KEYS.ADMIN_SETTINGS,
    matches: (path) =>
      path.startsWith("/general-settings") ||
      path.startsWith("/website-settings") ||
      path.startsWith("/app-settings") ||
      path.startsWith("/system-settings") ||
      path.startsWith("/financial-settings") ||
      path.startsWith("/others-settings"),
  },
];

export const ADMIN_REPORTS_ROUTE_RULES = [
  {
    permission: "Reports.Read",
    moduleKey: MODULE_KEYS.ADMIN_REPORTS,
    matches: (path) => path.startsWith("/reports"),
  },
];

export function isAdminAssetsMenuValue(menuValue) {
  return Object.prototype.hasOwnProperty.call(
    ADMIN_ASSETS_MENU_PERMISSIONS,
    menuValue,
  );
}

export function isAdminSettingsMenuValue(menuValue) {
  return Object.prototype.hasOwnProperty.call(
    ADMIN_SETTINGS_MENU_PERMISSIONS,
    menuValue,
  );
}

export function isAdminReportsMenuValue(menuValue) {
  return Object.prototype.hasOwnProperty.call(
    ADMIN_REPORTS_MENU_PERMISSIONS,
    menuValue,
  );
}

export function adminAssetsPermissionForMenu(menuValue) {
  return ADMIN_ASSETS_MENU_PERMISSIONS[menuValue] || null;
}

export function adminSettingsPermissionForMenu(menuValue) {
  return ADMIN_SETTINGS_MENU_PERMISSIONS[menuValue] || null;
}

export function adminReportsPermissionForMenu(menuValue) {
  return ADMIN_REPORTS_MENU_PERMISSIONS[menuValue] || null;
}

export function matchAdminAssetsRoute(path) {
  if (!path?.startsWith("/asset")) return null;
  return ADMIN_ASSETS_ROUTE_RULES.find((rule) => rule.matches(path)) || null;
}

export function matchAdminSettingsRoute(path) {
  if (
    !path?.startsWith("/user-management") &&
    !path?.startsWith("/general-settings") &&
    !path?.startsWith("/website-settings") &&
    !path?.startsWith("/app-settings") &&
    !path?.startsWith("/system-settings") &&
    !path?.startsWith("/financial-settings") &&
    !path?.startsWith("/others-settings")
  ) {
    return null;
  }
  return ADMIN_SETTINGS_ROUTE_RULES.find((rule) => rule.matches(path)) || null;
}

export function matchAdminReportsRoute(path) {
  if (!path?.startsWith("/reports")) return null;
  return ADMIN_REPORTS_ROUTE_RULES.find((rule) => rule.matches(path)) || null;
}

export function canShowAdminAssetsMenuForEmployee(menuValue, hasPermission) {
  const permission = adminAssetsPermissionForMenu(menuValue);
  if (!permission) return false;
  return hasPermission(permission);
}

export function canShowAdminSettingsMenuForEmployee(menuValue, hasPermission) {
  const permission = adminSettingsPermissionForMenu(menuValue);
  if (!permission) return false;
  return hasPermission(permission);
}

export function canShowAdminReportsMenuForEmployee(menuValue, hasPermission) {
  const permission = adminReportsPermissionForMenu(menuValue);
  if (!permission) return false;
  return hasPermission(permission);
}

export function canAccessAdminAssetsRoute(path, hasPermission) {
  const rule = matchAdminAssetsRoute(path);
  if (!rule) return path === "/asset" || path === "/asset/";
  return hasPermission(rule.permission);
}

export function canAccessAdminSettingsRoute(path, hasPermission) {
  const rule = matchAdminSettingsRoute(path);
  if (!rule) {
    return (
      path === "/user-management" ||
      path === "/user-management/" ||
      path.startsWith("/general-settings")
    );
  }
  return hasPermission(rule.permission);
}

export function canAccessAdminReportsRoute(path, hasPermission) {
  const rule = matchAdminReportsRoute(path);
  if (!rule) return path === "/reports" || path === "/reports/";
  return (
    hasPermission(rule.permission) ||
    hasPermission("Payroll.Read") ||
    hasPermission("Reports.Export")
  );
}

export function canAccessAdminRoutes(hasPermission) {
  return (
    hasPermission("User.Read") ||
    hasPermission("Role.Read") ||
    hasPermission("Asset.Read") ||
    hasPermission("Reports.Read") ||
    hasPermission("Reports.Export") ||
    hasPermission("Payroll.Read")
  );
}
